import $ from 'jquery'
import _ from 'lodash'
import ScorecardNode from './scorecard-node'
import Overview from './overview/scorecards-overview'
import Edit from './edit/scorecards-edit'
import Metrics from './metrics/metrics'
import BaseRouter from 'scoreboard/components/base-router'
import globals from 'scoreboard/components/globals'
import JsonStreamer from 'scoreboard/components/json-streamer'
import Nav from './scorecards-nav'
import appDisplay from 'scoreboard/components/app-display'
import moment from 'moment'
import spectator from 'scoreboard/components/spectator'
import ExportPdf from './export/scorecards-overview-export-pdf'
import EmptyState from 'scoreboard/components/empty-state'
import noPermission from 'scoreboard/components/no-permission.hbs'
import permissions from 'scoreboard/components/permissions'
import streamLoader from 'scoreboard/components/export/stream-loader'
import i18n from 'scoreboard/components/i18n'
import conductor from 'scoreboard/components/conductor'

/**
 * @extends BaseRouter
 */
var ScorecardsRouter = BaseRouter.extend(/** @lends ScorecardsRouter.prototype */ {

	routes: {
		'scorecards/:id(/)': 'overview',
		'scorecards/:id/note/:noteId(/)': 'overviewNote',
		'scorecards/:id/note/:noteId/edit(/)': 'overviewEditNote',
		'scorecards/:id/note/:noteId/reply/:replyId/edit': 'overviewEditNoteReply',
		'scorecards/:id/note/:noteId/reply/:replyId/delete(/)': 'overviewDeleteNote',
		'scorecards/:id/note(/)': 'overviewNewNote',
		'scorecards/:id/edit(/)': 'edit',
		'scorecards/:parentId/new(/)': 'create',
		'scorecards/:id/metrics(/)': 'metrics',
		'scorecards/:id/metrics/export(/)': 'metricsExport',
		'scorecards/:id/export(/)': 'exportPdf'
	},

	Nav: Nav,
	Model: ScorecardNode,
	section: 'scorecards',

	/**
	 * @param id The scorecard ID
	 * @param [options] All other values - must be separate from id to avoid route breakage
	 * @param [options.noteId] The item note ID. Exact meaning stipulated in noteAction
	 * @param {'view' | 'edit' | 'new' | 'delete'} [options.noteAction] Stipulates what to do with the note
	 * @param [options.replyId] If present, indicates that the specified note reply is to be edited instead of the note itself
	 */
	overview: function(id, options) {
		var { noteId, noteAction, replyId } = (options || {})

		if (this.handleNoCalendars({ heading: i18n.get('scorecards.scorecards') })) {
			return
		}

		var displayOptions = _.extend({
			calendarPeriodId: globals.periodId
		}, Overview.loadOptions)

		const modelPromise = this.load(id, displayOptions)
		appDisplay.display(modelPromise, model => {
			return new Overview({model, noteId, replyId, noteAction})
		})
		this.navForModel({ id }, modelPromise)
	},

	overviewNote: function(id, noteId) {
		this.overview(id, {noteId, noteAction: 'view'})
		conductor.navigate(`scorecards/${id}`)
	},

	overviewEditNote: function(id, noteId) {
		this.overview(id,{noteId, noteAction: 'edit'})
		conductor.navigate(`scorecards/${id}`)
	},

	overviewEditNoteReply: function(id, noteId, replyId) {
		this.overview(id, {noteId, replyId, noteAction: 'edit'})
		conductor.navigate(`scorecards/${id}`)
	},

	overviewDeleteNote: function(id, noteId, replyId) {
		this.overview(id, {noteId, replyId, noteAction: 'delete'})
		conductor.navigate(`scorecards/${id}`)
	},

	overviewNewNote: function(id) {
		this.overview(id, {noteAction: 'new'})
		conductor.navigate(`scorecards/${id}`)
	},

	edit: function(id) {
		if (this.handleNoCalendars({ heading: i18n.get('scorecards.scorecards') })) {
			return
		}

		var options = {
			owners: true,
			updaters: true,
			tags: true,
			thresholdOverrideData: true,
			// #10389 the calendar period is needed so that the descendant
			// lowest score is returned, which is required to show the red
			// metrics indicator on the metrics tab
			calendarPeriodId: globals.periodId,
			percentWeightingIgnoringPerformance: true,
			alertSubscriptions: true,
			datasets: true
		}
		const modelPromise = this.load(id, options)
		appDisplay.display(modelPromise, function(model) {
			var editable = permissions.structure(model.get('organizationId')) ||
				permissions.ownersUpdaters(model.get('organizationId'))
			return editable ? new Edit({ model }) :
				new EmptyState({ body : noPermission() })
		})
		this.navForModel({ id, edit: true }, modelPromise)
	},

	create: function (parentId) {
		if (this.handleNoCalendars({ heading: i18n.get('scorecards.scorecards') })) {
			return
		}

		// this has to come first so that the navView is defined
		this.nav({ id: parentId, toggleEditViewState: true })
		var url = 'api/scorecards/' + parentId + '/new-node-defaults'
		appDisplay.display($.get(url), defaults => {
			if (!spectator.getCurrent()) {
				globals.orgId = defaults.organizationId
			}
			defaults.parentId = parentId
			var model = new ScorecardNode(defaults)
			var edit = new Edit({ model: model })
			this.addTransient(edit, parentId, model.attributes)
			return edit
		})
	},

	/**
	 * @param id
	 */
	metrics: function(id) {
		if (this.handleNoCalendars({ heading: i18n.get('scorecards.scorecards') })) {
			return
		}

		const params = $.param({
			calendarPeriodId: globals.periodId,
			alertSubscriptions: true
		})
		const start = moment()
		const view = new Metrics({
			start: start,
			model: new ScorecardNode({id: id, organizationId: globals.orgId})
		})

		const modelPromise = new Promise((resolve) => {
			const streamer = JsonStreamer.initiate(view, {
				stream: 'metrics',
				url: `api/scorecards/${id}/metrics?${params}`
			},{
				dataFn: (key,value) => {
					if (key === 'scorecardNode') {
						resolve(new ScorecardNode(value))
					}
				}
			})
			// #12995 once the headers are available, make sure the edit tab is
			// displayed properly
			streamer.xhrStream.on('headers', function() {
				permissions.update(streamer.xhrStream.xhr)
			})
		})

		appDisplay.display(view)

		// change the nav AFTER we get the stream started
		this.navForModel({ id }, modelPromise)

		return view
	},

	metricsExport: function(id) {
		var view = this.metrics(id)
		view.on('visible', function() {
			process.nextTick(streamLoader.bind(undefined, view, view.stream))
		})
	},

	exportPdf: function(id) {
		var loadOptions = {
			calendarPeriodId: globals.periodId,
			relatedItems: true,
			notes: true,
			dataUsedInCalculations: true,
			chart: true,
			scores: true,
			owners: true,
			updaters: true,
			tags: true,
			percentWeightingIgnoringPerformance: true
		}
		var promise = this.load(id, loadOptions)
		appDisplay.display(promise, function (model) {
			return new ExportPdf({model: model})
		}.bind(this))
		return promise
	}

})

export default ScorecardsRouter

ScorecardsRouter.__filename__ = 'scorecards/scorecards-router.js'
